import {
  BrainCircuit,
  Building2,
  ChartNoAxesCombined,
  FileStack,
  Files,
  HandCoins,
  Landmark,
  Newspaper,
  Radar,
  WalletCards,
} from "lucide-react";

import type { ModuleDefinition } from "@/types/analytics";
import type { NavigationItem, NavigationSection } from "@/types/navigation";

export const navigationSections: NavigationSection[] = [
  {
    title: "Platform",
    items: [
      {
        title: "Regional Intelligence",
        path: "/regional-intelligence",
        description: "Ruang kerja regional utama yang holistik",
        icon: Radar,
        audience: ["Eksekutif", "Analis", "Operasional"],
      },
      {
        title: "Media Analytics",
        path: "/media-analytics",
        description: "Pemantauan narasi dan korelasi",
        icon: Newspaper,
        audience: ["Eksekutif", "Analis"],
      },
      {
        title: "Executive Briefs",
        path: "/executive-briefs",
        description: "Koleksi dan telaah brief yang dihasilkan",
        icon: FileStack,
        audience: ["Eksekutif", "Analis", "Operasional"],
      },
    ],
  },
  {
    title: "Dashboard",
    items: [
      {
        title: "TKD",
        path: "/tkd",
        description: "Analitik transfer ke daerah",
        icon: WalletCards,
        audience: ["Eksekutif", "Analis"],
      },
      {
        title: "Dana Desa",
        path: "/dana-desa",
        description: "Pemantauan Dana Desa",
        icon: Landmark,
        audience: ["Eksekutif", "Analis"],
      },
      {
        title: "APBN",
        path: "/apbn",
        description: "Analisis eksekusi anggaran dan BA",
        icon: Files,
        audience: ["Analis", "Operasional"],
      },
      {
        title: "IKPA",
        path: "/ikpa",
        description: "Indikator kinerja perbendaharaan",
        icon: Building2,
        audience: ["Analis", "Operasional"],
      },
      {
        title: "KUR",
        path: "/kur",
        description: "Analitik kredit bersubsidi",
        icon: HandCoins,
        audience: ["Eksekutif", "Analis"],
      },
      {
        title: "Makro",
        path: "/macro",
        description: "Indikator makro regional",
        icon: ChartNoAxesCombined,
        audience: ["Eksekutif", "Analis"],
      },
    ],
  },
  {
    title: "Perangkat",
    items: [
      {
        title: "Lab AI",
        path: "/ai",
        description: "Ruang kerja prompt, SQL, dan brief lanjutan",
        icon: BrainCircuit,
        audience: ["Eksekutif", "Analis", "Operasional"],
      },
    ],
  },
];

export const navigationItems: NavigationItem[] = navigationSections.flatMap(
  (section) => section.items,
);

export const moduleDefinitions: ModuleDefinition[] = [
  {
    id: "dashboard",
    title: "Dasbor",
    eyebrow: "Tampilan komando nasional",
    summary: "Papan sinyal lintas modul untuk intelijen fiskal, regional, dan media.",
    chartPrimaryLabel: "Sinyal nasional",
    chartSecondaryLabel: "Perbandingan regional",
  },
  {
    id: "fiscal",
    title: "Fiskal",
    eyebrow: "Intelijen fiskal",
    summary: "Bandingkan APBN, TKD, Dana Desa, dan tren kinerja dalam satu narasi fiskal.",
    chartPrimaryLabel: "Pergerakan fiskal",
    chartSecondaryLabel: "Kualitas pelaksanaan",
  },
  {
    id: "tkd",
    title: "TKD",
    eyebrow: "Pemantauan transfer",
    summary: "Lacak pagu, realisasi, dan distribusi transfer lintas daerah dan tahun.",
    chartPrimaryLabel: "Tren transfer",
    chartSecondaryLabel: "Komposisi regional",
  },
  {
    id: "dana-desa",
    title: "Dana Desa",
    eyebrow: "Intelijen Dana Desa",
    summary: "Pantau realisasi bulanan, konteks anggaran tahunan, dan cakupan penerima.",
    chartPrimaryLabel: "Realisasi bulanan",
    chartSecondaryLabel: "Cakupan desa",
  },
  {
    id: "apbn",
    title: "APBN",
    eyebrow: "Pelaksanaan anggaran kelembagaan",
    summary: "Evaluasi pelaksanaan BA, satker, KPPN, dan kanwil dengan lapisan kualitas data.",
    chartPrimaryLabel: "Pelaksanaan anggaran",
    chartSecondaryLabel: "Komposisi kelembagaan",
  },
  {
    id: "ikpa",
    title: "IKPA",
    eyebrow: "Kinerja operasional",
    summary: "Identifikasi perubahan kinerja satker, KPPN, dan kanwil dalam satu tampilan bulanan.",
    chartPrimaryLabel: "Tren kinerja",
    chartSecondaryLabel: "Sebaran operasional",
  },
  {
    id: "kur",
    title: "KUR",
    eyebrow: "Dukungan kredit regional",
    summary: "Ikuti subsidi dan cakupan penerima pada level provinsi maupun kabupaten/kota.",
    chartPrimaryLabel: "Tren subsidi",
    chartSecondaryLabel: "Pergerakan penerima",
  },
  {
    id: "macro",
    title: "Makro",
    eyebrow: "Tampilan pembangunan regional",
    summary: "Bandingkan IPM, PDRB, kemiskinan, dan pengangguran dalam satu profil provinsi.",
    chartPrimaryLabel: "Indeks makro",
    chartSecondaryLabel: "Tekanan sosial",
  },
  {
    id: "media",
    title: "Media",
    eyebrow: "Intelijen naratif",
    summary: "Lihat bagaimana narasi regional selaras dengan pola fiskal dan makro.",
    chartPrimaryLabel: "Aktivitas narasi",
    chartSecondaryLabel: "Keragaman sumber",
  },
  {
    id: "ai",
    title: "AI",
    eyebrow: "Penasihat strategis",
    summary: "Hasilkan ringkasan, perbandingan, dan brief bergaya eksekutif dari sinyal terstruktur.",
    chartPrimaryLabel: "Penggunaan penasihat",
    chartSecondaryLabel: "Kualitas respons",
  },
  {
    id: "governance",
    title: "Tata Kelola",
    eyebrow: "Lapisan audit dan kontrol",
    summary: "Pantau cakupan, referensi yang belum terselesaikan, jejak audit AI, dan pengecualian operasional.",
    chartPrimaryLabel: "Tren isu",
    chartSecondaryLabel: "Pergerakan penyelesaian",
  },
];

export const moduleDefinitionMap = new Map(
  moduleDefinitions.map((item) => [item.id, item]),
);
